import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnInit,
  computed,
  inject,
  signal,
} from '@angular/core';
import { NgbActiveOffcanvas, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { SimplebarAngularModule } from 'simplebar-angular';
import { finalize } from 'rxjs';
import {
  WorkspaceAssetModel,
  WorkspaceAssetService,
} from '@core/services/workspace-asset.service';
import { AuthService, PermissionFlag } from '@core/services/auth.service';
import { DeleteConfirmModal } from '@/app/views/modals/delete-confirm-modal/delete-confirm-modal';
import { WorkspaceAssetModal } from '@/app/views/modals/workspace-asset-modal/workspace-asset-modal';

@Component({
  selector: 'app-source',
  imports: [SimplebarAngularModule],
  templateUrl: './source.html',
  styleUrl: './source.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class Source implements OnInit {
  private readonly offcanvas = inject(NgbActiveOffcanvas);
  private readonly modalService = inject(NgbModal);
  private readonly workspaceAssetService = inject(WorkspaceAssetService);
  private readonly authService = inject(AuthService);
  private readonly cdr = inject(ChangeDetectorRef);

  readonly files = signal<WorkspaceAssetModel[]>([]);
  readonly isLoading = signal(false);
  readonly isDeleting = signal(false);
  readonly errorMessage = signal<string | null>(null);
  readonly canRead = this.authService.hasPermission(
    'SourceAsset',
    PermissionFlag.Read,
  );
  readonly canCreate = this.authService.hasPermission(
    'SourceAsset',
    PermissionFlag.Create,
  );
  readonly canDelete = this.authService.hasPermission(
    'SourceAsset',
    PermissionFlag.Delete,
  );

  readonly visibleFiles = computed(() =>
    this.files()
      .filter((file) => !file.deletedAt)
      .sort((left, right) => {
        const leftTime = this.toTimestamp(left.createdAt);
        const rightTime = this.toTimestamp(right.createdAt);

        if (leftTime === rightTime) {
          return right.id - left.id;
        }

        return rightTime - leftTime;
      }),
  );

  ngOnInit(): void {
    if (this.canRead) {
      this.loadFiles();
    }
  }

  close(): void {
    this.offcanvas.dismiss();
  }

  onAdd(): void {
    if (!this.canCreate) {
      return;
    }

    const modalRef = this.modalService.open(WorkspaceAssetModal, {
      size: 'lg',
      backdrop: 'static',
    });
    modalRef.closed.subscribe(
      (result: { importedCount: number; failedCount: number } | undefined) => {
        if (!result || result.importedCount === 0) {
          return;
        }

        this.loadFiles();
      },
    );
  }

  confirmDelete(file: WorkspaceAssetModel): void {
    if (!this.canDelete || this.isDeleting()) {
      return;
    }

    const modalRef = this.modalService.open(DeleteConfirmModal, {
      centered: true,
      backdrop: 'static',
      windowClass: 'modal-alert modal-delete-alert',
    });

    modalRef.componentInstance.itemName = file.originalFileName;
    modalRef.componentInstance.itemTypeLabel = 'fonte';

    modalRef.closed.subscribe((confirmed: boolean) => {
      if (!confirmed) {
        return;
      }

      this.deleteFile(file);
    });
  }

  private loadFiles(): void {
    this.isLoading.set(true);
    this.errorMessage.set(null);

    this.workspaceAssetService
      .list()
      .pipe(
        finalize(() => {
          this.isLoading.set(false);
          this.cdr.markForCheck();
        }),
      )
      .subscribe({
        next: (files) => {
          this.files.set(files);
        },
        error: (error: Error) => {
          this.errorMessage.set(
            error?.message ?? 'Nao foi possivel carregar as fontes.',
          );
        },
      });
  }

  private deleteFile(file: WorkspaceAssetModel): void {
    this.isDeleting.set(true);
    this.errorMessage.set(null);

    this.workspaceAssetService
      .delete(file.id)
      .pipe(
        finalize(() => {
          this.isDeleting.set(false);
          this.cdr.markForCheck();
        }),
      )
      .subscribe({
        next: () => this.loadFiles(),
        error: (error: Error) => {
          this.errorMessage.set(
            error?.message ?? 'Nao foi possivel excluir a fonte.',
          );
        },
      });
  }

  private toTimestamp(value: string | null | undefined): number {
    if (!value) {
      return 0;
    }

    const timestamp = Date.parse(value);
    return Number.isFinite(timestamp) ? timestamp : 0;
  }
}
